import axios from 'axios';
import {
  useCompleteArtifactMediaUploadMutation,
  useCreateArtifactMediaUploadMutation
} from 'api/ArtifactsApi';
import { ArtifactMediaKind } from 'components/Artifacts/types';
import { useEffect, useRef, useState } from 'react';

import { ArtifactMediaFieldState } from './editorTypes';

type UseArtifactMediaUploadProps = {
  mediaKind: ArtifactMediaKind;
  initialMediaUrl?: string | null;
  initialMediaId?: string | null;
};

export const useArtifactMediaUpload = ({ mediaKind, initialMediaId, initialMediaUrl }: UseArtifactMediaUploadProps) => {
  const [createUploadIntent] = useCreateArtifactMediaUploadMutation();
  const [completeUploadIntent] = useCompleteArtifactMediaUploadMutation();
  const abortControllerRef = useRef<AbortController | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [state, setState] = useState<ArtifactMediaFieldState>({
    media: initialMediaId && initialMediaUrl ? { id: initialMediaId, url: initialMediaUrl, status: 'READY' } : null,
    previewUrl: initialMediaUrl || null,
    phase: initialMediaId && initialMediaUrl ? 'ready' : 'idle',
    progressPercent: 0,
    errorMessage: '',
    fileName: ''
  });

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (previewUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const updatePreview = (nextPreviewUrl: string | null) => {
    if (previewUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    previewUrlRef.current = nextPreviewUrl;
    setState((current) => ({ ...current, previewUrl: nextPreviewUrl }));
  };

  const reset = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    updatePreview(null);
    setState({
      media: null,
      previewUrl: null,
      phase: 'idle',
      progressPercent: 0,
      errorMessage: '',
      fileName: ''
    });
  };

  const setError = (message: string) => {
    setState((current) => ({
      ...current,
      phase: 'error',
      progressPercent: 0,
      errorMessage: message
    }));
  };

  const uploadFile = async (file: File) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const previewUrl = URL.createObjectURL(file);
    updatePreview(previewUrl);
    setState((current) => ({
      ...current,
      phase: 'uploading',
      progressPercent: 0,
      errorMessage: '',
      fileName: file.name
    }));

    try {
      const rawContentType = file.type || 'application/octet-stream';
      const contentType = rawContentType.split(';')[0].trim();
      const uploadIntent = await createUploadIntent({
        mediaKind,
        contentType,
        fileName: file.name,
        fileSizeBytes: file.size
      }).unwrap();

      if (!uploadIntent.uploadUrl) {
        setError('The upload URL was not returned.');
        return null;
      }

      if (uploadIntent.uploadFields && Object.keys(uploadIntent.uploadFields).length) {
        const formData = new FormData();
        Object.entries(uploadIntent.uploadFields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', file);

        await axios.post(uploadIntent.uploadUrl, formData, {
          signal: controller.signal,
          onUploadProgress: (event) => {
            const progress = event.progress ? Math.round(event.progress * 100) : 0;
            setState((current) => ({ ...current, progressPercent: progress }));
          }
        });
      } else {
        await axios.put(uploadIntent.uploadUrl, file, {
          headers: {
            'Content-Type': contentType
          },
          signal: controller.signal,
          onUploadProgress: (event) => {
            const progress = event.progress ? Math.round(event.progress * 100) : 0;
            setState((current) => ({ ...current, progressPercent: progress }));
          }
        });
      }

      const uploadStatus = await completeUploadIntent({ uploadId: uploadIntent.id }).unwrap();
      const resolvedMediaId = uploadStatus.media?.id || uploadStatus.id || uploadIntent.id;

      if (uploadStatus.status === 'FAILED' || !resolvedMediaId) {
        setError('The uploaded media is still unavailable. Please try again.');
        return null;
      }

      const resolvedMedia =
        uploadStatus.media ||
        ({
          id: resolvedMediaId,
          url: previewUrl,
          thumbnailUrl: null,
          mimeType: rawContentType,
          durationSec: null,
          status: uploadStatus.status === 'PENDING_UPLOAD' ? 'UPLOADED' : uploadStatus.status,
          errorCode: uploadStatus.errorCode || null
        } as const);

      updatePreview(previewUrl);
      setState((current) => ({
        ...current,
        media: resolvedMedia,
        previewUrl,
        phase: 'ready',
        progressPercent: 100,
        errorMessage: ''
      }));

      return resolvedMedia;
    } catch (error: any) {
      if (axios.isCancel(error) || error?.name === 'CanceledError') {
        setState((current) => ({ ...current, phase: 'idle', progressPercent: 0 }));
        return null;
      }

      setError(error?.message || 'We could not upload this file. Please try again.');
      return null;
    }
  };

  return {
    state,
    uploadFile,
    reset,
    setError
  };
};